<template>
  <div>
    <h3 class="text-lg font-medium leading-6 text-cblack">
      {{ $t('consentModal.heading') }}
    </h3>
    <div class="mt-2 space-y-3 text-sm text-gray-900">
      <p>
        {{ $t('consentModal.p1') }}
      </p>

      <i18n-t keypath="consentModal.p2" tag="p">
        <template #link>
          <NuxtLink
            to="/datenschutz"
            target="_blank"
            class="font-semibold underline text-cblack"
            >{{ $t('consentModal.link') }}</NuxtLink
          >
        </template>
      </i18n-t>
    </div>

    <div v-if="isSettingsEnabled" class="my-6">
      <UAccordion
        :items="consentList"
        type="multiple"
        :ui="{ content: 'pb-3 pr-2' }"
      >
        <template #content="{ item }">
          <div v-if="item.value === 'necessary'" class="text-sm">
            <p class="mb-2 text-primary font-medium text-sm">
              {{ $t('consentModal.alwaysActive') }}
            </p>

            <p>{{ item.content }}</p>
          </div>

          <USwitch
            v-else
            v-model="consentOptions![item.value!]"
            size="xl"
            :label="item.content"
            :ui="{
              label: 'text-sm font-normal',
              container: 'mt-1',
            }"
          />
        </template>
      </UAccordion>
    </div>

    <div
      class="grid grid-cols-1 gap-3 mx-auto mt-5 max-w-50 sm:max-w-full sm:flex sm:justify-between sm:gap-3"
    >
      <VButton
        :size="Size.sm"
        :variant="Variant.secondary"
        class="sm:w-1/3 justify-center"
        @click="onClickAcceptButton"
      >
        {{ $t('consentModal.acceptAll') }}
      </VButton>

      <VButton
        :size="Size.sm"
        :variant="Variant.secondary"
        class="sm:w-1/3 justify-center"
        @click="onClickRejectButton"
      >
        {{ $t('consentModal.declineAll') }}
      </VButton>

      <VButton
        :size="Size.sm"
        :variant="Variant.link"
        class="sm:w-1/3 justify-center"
        @click="onClickSettingsButton"
      >
        {{ $t(isSettingsEnabled ? 'consentModal.save' : 'consentModal.edit') }}
      </VButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Size, Variant } from '@/types/enums'
import type { AccordionItem } from '@nuxt/ui'

type ConsentModel = {
  necessary: boolean
  analytics: boolean
} & Record<string, boolean>

const emit = defineEmits<{
  (e: 'accept-all'): void
  (e: 'decline-all'): void
  (e: 'save'): void
}>()

const consentOptions = defineModel<ConsentModel>('consent')

const { t } = useI18n()
const isSettingsEnabled = ref(false)

const consentList = computed<AccordionItem[]>(() => {
  if (!consentOptions.value) return []
  return Object.keys(consentOptions.value).map((option) => ({
    label: t(`consentModal.options.${option}.title`),
    content: t(`consentModal.options.${option}.description`),
    value: option,
  }))
})

function onClickAcceptButton() {
  emit('accept-all')
}

function onClickRejectButton() {
  emit('decline-all')
}

function onClickSettingsButton() {
  if (isSettingsEnabled.value) {
    emit('save')
  } else {
    isSettingsEnabled.value = true
  }
}
</script>
